import React, {useEffect, useState} from 'react';
import {
  Box,
  Flex,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Link,
  Tag,
  Button,
  HStack,
  Spinner,
  Alert,
  Tooltip,
  Divider,
} from '@chakra-ui/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getPaginationRowModel,
} from '@tanstack/react-table';
import {
  useCustomer,
  useIsFreeOrListBuilderCustomer,
} from '../../hooks/api/metadata';
import {PlanLabels} from '../../shared/customer';
import {useAccountEnrichmentRedemptions} from '../../hooks/api/credits';
import {
  FieldDefinitionTypeLabels,
  getFieldLabel,
} from '../../shared/fields/types';
import {Information} from '@carbon/icons-react';
import {CreditDisplay} from '../App/CreditUsage';
import {CreditCostTag} from '../App/CreditCostTag';
import {AccountEnrichmentRedemptionMaxResults} from '../../shared/api/definitions/root';

export const PlanAndCredits = () => {
  const customer = useCustomer();
  const isFreeOrListBuilder = useIsFreeOrListBuilderCustomer();

  return (
    <Box>
      <Flex alignItems="center" gap="2" mb="6">
        <Text fontSize="xl" fontWeight="500">
          Your Plan
        </Text>
      </Flex>

      <Flex
        direction="column"
        gap="4"
        p="4"
        borderRadius="lg"
        bg="white"
        border="1px solid"
        borderColor="kgray.200"
        mb="4"
      >
        <Flex justifyContent="space-between" alignItems="center">
          <Flex direction="column" gap="1">
            <Text fontSize="lg" fontWeight="600">
              {PlanLabels[customer.plan.type]}
            </Text>
            <Text color="kgray.300" fontSize="sm">
              Current subscription
            </Text>
          </Flex>

          {customer.plan.type === 'listBuilder' && (
            <Flex
              alignItems="center"
              gap="2"
              bg="kblue.50"
              p="2"
              borderRadius="md"
            >
              <Text fontWeight="500">Expires:</Text>
              <Text fontWeight="medium">
                {new Date(customer.plan.endDate).toLocaleDateString()}
              </Text>
              <Tooltip
                label="Contact us to extend your plan's end date"
                placement="top"
                hasArrow
              >
                <Information color="kblue.400" />
              </Tooltip>
            </Flex>
          )}
        </Flex>

        {isFreeOrListBuilder ? (
          <>
            <Divider />
            <CreditDisplay />
          </>
        ) : (
          <>
            <Flex
              alignItems="center"
              gap="3"
              p="2"
              borderRadius="md"
              bg="kblue.50"
            >
              <Information color="kblue.400" />
              <Text>
                <Link
                  color="kblue.500"
                  fontWeight="medium"
                  href="mailto:support@keyplay.io"
                >
                  Contact Keyplay
                </Link>{' '}
                to make changes to your plan.
              </Text>
            </Flex>
          </>
        )}
      </Flex>

      {!isFreeOrListBuilder && (
        <Box mt="10">
          <CreditUsageTable />
        </Box>
      )}
    </Box>
  );
};

type CreditRedemptionRowData = {
  timestamp: Date;
  amount: number;
  state: 'pending' | 'completed';
  name: string;
  type: string;
};

const columnHelper = createColumnHelper<CreditRedemptionRowData>();
const columns = [
  columnHelper.accessor('name', {
    header: 'FIELD NAME',
    cell: (info) => {
      return <Box>{info.getValue()}</Box>;
    },
    size: 300,
    minSize: 300,
    maxSize: 300,
  }),
  columnHelper.accessor('type', {
    header: 'TYPE',
    cell: (info) => {
      return <Box>{info.getValue()}</Box>;
    },
    size: 100,
    minSize: 100,
    maxSize: 100,
  }),
  columnHelper.accessor((info) => info, {
    header: 'CREDITS USED',
    cell: (info) => {
      return (
        <Box>
          {info.getValue().state === 'pending' ? (
            <Tag
              key="default"
              size={'md'}
              fontWeight={500}
              fontSize={12}
              textTransform="uppercase"
            >
              Pending
            </Tag>
          ) : (
            <CreditCostTag creditCost={info.getValue().amount} />
          )}
        </Box>
      );
    },
    size: 100,
    minSize: 100,
    maxSize: 100,
  }),
  columnHelper.accessor('timestamp', {
    header: 'DATE',
    cell: (info) => {
      const date = info.getValue();
      const formattedDate = date.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
      });
      const formattedTime = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: 'numeric',
      });
      return (
        <Box color="kgray.300">
          {formattedDate}, {formattedTime}
        </Box>
      );
    },
    size: 180,
    minSize: 150,
    maxSize: 200,
  }),
];

const CreditUsageTable = () => {
  const customer = useCustomer();
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<CreditRedemptionRowData[] | null>(null);
  const [numRedemptions, setNumRedemptions] = useState(0);
  const pageSize = AccountEnrichmentRedemptionMaxResults;

  const {data, isError, isPending} = useAccountEnrichmentRedemptions({
    offset: page * pageSize,
  });

  useEffect(() => {
    if (data?.redemptions) {
      setNumRedemptions(data.totalRedemptions);

      setRows(
        data.redemptions.map(({timestamp, amount, state, purpose}) => {
          const field = customer.fieldDefinitions?.find((field) =>
            field.id.equals(purpose.fieldId)
          );

          return {
            timestamp,
            amount: amount * -1,
            state,
            name: field ? getFieldLabel(field) : 'Unknown Field',
            type: field?.type
              ? FieldDefinitionTypeLabels[field.type]
              : 'Unknown Type',
          };
        })
      );
    }
  }, [data, customer.fieldDefinitions]);

  const totalPages = Math.ceil(numRedemptions / pageSize);

  const table = useReactTable({
    columns,
    data: rows ?? [],
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    pageCount: totalPages,
    state: {
      pagination: {
        pageIndex: page,
        pageSize,
      },
    },
  });

  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };

  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  if (!rows) {
    return (
      <Flex justifyContent="center" alignItems="center" p="8">
        <Spinner />
      </Flex>
    );
  }

  if (isError) {
    return (
      <Alert status="error" borderRadius="md">
        Unable to load credit redemption data. Please try again later.
      </Alert>
    );
  }

  return (
    <Box>
      <HStack gap="4" mb="6">
        <Text fontSize="xl" fontWeight="500">
          Credits
        </Text>
        <CreditCostTag creditCost={customer.credits} />
      </HStack>

      {!!rows.length && (
        <>
          <TableContainer
            border="1px"
            borderColor="kgray.200"
            borderRadius="lg"
            mb="4"
            boxShadow="sm"
            overflow="hidden"
            width="100%"
          >
            <Table variant="simple" layout="fixed" width="100%">
              <Thead bg="kgray.100">
                {table.getHeaderGroups().map((headerGroup) => (
                  <Tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <Th
                        key={header.id}
                        py="4"
                        width={header.column.getSize()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </Th>
                    ))}
                  </Tr>
                ))}
              </Thead>
              <Tbody>
                {table.getRowModel().rows.map((row) => (
                  <Tr key={row.id} _hover={{bg: 'kgray.50'}}>
                    {row.getVisibleCells().map((cell) => (
                      <Td key={cell.id} py="4" width={cell.column.getSize()}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Td>
                    ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
          <Flex justifyContent="center" alignItems="center" mb="4">
            <HStack spacing="2">
              <Button
                onClick={handlePrevPage}
                isDisabled={page === 0}
                isLoading={isPending}
                size="sm"
                variant="ghost"
                colorScheme="kbuttonblue"
                leftIcon={
                  <Box as="span" transform="rotate(180deg)">
                    →
                  </Box>
                }
              >
                Previous
              </Button>
              <Flex
                alignItems="center"
                justifyContent="center"
                px="3"
                py="1"
                borderRadius="md"
                bg="kgray.100"
                minW="100px"
              >
                <Text fontWeight="medium">
                  {page + 1} / {Math.max(totalPages, 1)}
                </Text>
              </Flex>
              <Button
                onClick={handleNextPage}
                isDisabled={page >= totalPages - 1 || numRedemptions === 0}
                isLoading={isPending}
                size="sm"
                variant="ghost"
                colorScheme="kbuttonblue"
                rightIcon={<Box as="span">→</Box>}
              >
                Next
              </Button>
            </HStack>
          </Flex>
        </>
      )}
    </Box>
  );
};
